"use client";

import React, { useEffect, useState } from "react";
import {
  Activity,
  Award,
  BookOpen,
  Boxes,
  Building2,
  Calendar,
  CalendarDays,
  Check,
  Clock,
  Dumbbell,
  GraduationCap,
  HeartHandshake,
  Loader2,
  Lock,
  LogOut,
  MapPin,
  MessageSquare,
  Moon,
  Plus,
  QrCode,
  Search,
  Send,
  Shield,
  Sparkles,
  Sun,
  Trophy,
  UserCheck,
  UserPlus,
  Users,
  Utensils,
  Volleyball,
  Wallet,
  Zap,
} from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTheme } from "next-themes";
import { useRouter } from "next/navigation";
import { toast } from "sonner";

import { apiClient, extractErrorMessage } from "@/lib/api-client";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  useAvailability,
  useCreateBooking,
  useMarketplaceUnits,
  useMyBookings,
  usePayBooking,
} from "@/hooks/use-bookings";
import { roleLabel, useAuthStore } from "@/lib/auth-store";
import {
  Bootcamp,
  ChatMessage,
  ChatRoom,
  CommunityPost,
  CommunitySquad,
  MarketplaceUnit,
  PublicSchool,
} from "@/lib/types";

export default function ExploreMarketplacePage() {
  const router = useRouter();
  const { status, user, hydrate, logout } = useAuthStore();
  const { theme, setTheme } = useTheme();
  const queryClient = useQueryClient();

  const [activeMainTab, setActiveMainTab] = useState<string>("facilities");
  const [query, setQuery] = useState("");
  const [selectedSchool, setSelectedSchool] = useState<PublicSchool | null>(null);

  // Squads State
  const [squadSportFilter, setSquadSportFilter] = useState<string>("ALL");
  const [isCreateSquadOpen, setIsCreateSquadOpen] = useState<boolean>(false);
  const [newSquadName, setNewSquadName] = useState<string>("");
  const [newSquadSport, setNewSquadSport] = useState<string>("Football");
  const [newSquadDesc, setNewSquadDesc] = useState<string>("");
  const [newSquadSkill, setNewSquadSkill] = useState<string>("All Levels");
  const [newSquadGround, setNewSquadGround] = useState<string>("DPS Main Ground");
  const [joiningSquad, setJoiningSquad] = useState<CommunitySquad | null>(null);
  const [joinMessage, setJoinMessage] = useState<string>("Hey! Would love to join your squad for weekend matches.");

  // Chat State
  const [activeRoomId, setActiveRoomId] = useState<string | null>(null);
  const [chatInput, setChatInput] = useState<string>("");

  // Feed / Social Wall State
  const [isCreatePostOpen, setIsCreatePostOpen] = useState<boolean>(false);
  const [postTitle, setPostTitle] = useState<string>("");
  const [postContent, setPostContent] = useState<string>("");
  const [postSport, setPostSport] = useState<string>("Football");
  const [postPlayersNeeded, setPostPlayersNeeded] = useState<number>(2);
  const [postTime, setPostTime] = useState<string>("This Sunday 6:00 PM");

  useEffect(() => {
    if (status === "idle") hydrate();
  }, [status, hydrate]);

  useEffect(() => {
    if (status === "unauthenticated") {
      router.replace("/login");
    }
  }, [status, router]);

  // Fetch Public Schools
  const { data: schools, isLoading: schoolsLoading } = useQuery({
    queryKey: ["explore-schools", query],
    queryFn: async () => (await apiClient.get<PublicSchool[]>("/auth/schools/search", { params: { q: query } })).data,
    enabled: status === "authenticated",
  });

  // Auto select first school
  useEffect(() => {
    if (schools && schools.length > 0 && !selectedSchool) {
      setSelectedSchool(schools[0]);
    }
  }, [schools, selectedSchool]);

  // Fetch Community Squads
  const { data: squads, isLoading: squadsLoading } = useQuery<CommunitySquad[]>({
    queryKey: ["community-squads", squadSportFilter],
    queryFn: async () => {
      const params: any = {};
      if (squadSportFilter !== "ALL") params.sport_name = squadSportFilter;
      const res = await apiClient.get("/community/squads", { params });
      return res.data;
    },
    enabled: status === "authenticated",
  });

  // Fetch Bootcamps
  const { data: bootcamps, isLoading: bootcampsLoading } = useQuery<Bootcamp[]>({
    queryKey: ["community-bootcamps"],
    queryFn: async () => {
      const res = await apiClient.get("/community/bootcamps");
      return res.data;
    },
    enabled: status === "authenticated",
  });

  // Fetch Community Feed Posts
  const { data: feedPosts, isLoading: postsLoading } = useQuery<CommunityPost[]>({
    queryKey: ["community-posts"],
    queryFn: async () => {
      const res = await apiClient.get("/community/posts");
      return res.data;
    },
    enabled: status === "authenticated",
  });

  // Fetch Chat Rooms
  const { data: chatRooms, isLoading: roomsLoading } = useQuery<ChatRoom[]>({
    queryKey: ["community-chat-rooms"],
    queryFn: async () => {
      const res = await apiClient.get("/community/chat/rooms");
      return res.data;
    },
    enabled: status === "authenticated",
  });

  // Auto select first chat room if none
  useEffect(() => {
    if (chatRooms && chatRooms.length > 0 && !activeRoomId) {
      setActiveRoomId(chatRooms[0].id);
    }
  }, [chatRooms, activeRoomId]);

  // Fetch Active Room Messages
  const { data: activeMessages } = useQuery<ChatMessage[]>({
    queryKey: ["community-chat-messages", activeRoomId],
    queryFn: async () => {
      if (!activeRoomId) return [];
      const res = await apiClient.get(`/community/chat/rooms/${activeRoomId}/messages`);
      return res.data;
    },
    enabled: !!activeRoomId,
    refetchInterval: 5000,
  });

  // Create Squad Mutation
  const createSquadMutation = useMutation({
    mutationFn: async () => {
      const res = await apiClient.post("/community/squads", {
        name: newSquadName,
        sport_name: newSquadSport,
        description: newSquadDesc,
        skill_level: newSquadSkill,
        city: "New Delhi",
        home_ground: newSquadGround,
        is_private: false,
        max_members: 25,
      });
      return res.data;
    },
    onSuccess: () => {
      toast.success("Community Squad created! You are the Captain.");
      setIsCreateSquadOpen(false);
      queryClient.invalidateQueries({ queryKey: ["community-squads"] });
      setNewSquadName("");
      setNewSquadDesc("");
    },
    onError: (err) => {
      toast.error(extractErrorMessage(err, "Failed to create squad."));
    },
  });

  // Join Squad Mutation
  const joinSquadMutation = useMutation({
    mutationFn: async () => {
      if (!joiningSquad) return;
      const res = await apiClient.post(`/community/squads/${joiningSquad.id}/join`, {
        message: joinMessage,
      });
      return res.data;
    },
    onSuccess: () => {
      toast.success("Joined squad! Welcome to the team.");
      setJoiningSquad(null);
      queryClient.invalidateQueries({ queryKey: ["community-squads"] });
    },
    onError: (err) => {
      toast.error(extractErrorMessage(err, "Could not join squad."));
    },
  });

  // Register Bootcamp Mutation
  const registerBootcampMutation = useMutation({
    mutationFn: async (bootcampId: string) => {
      const res = await apiClient.post(`/community/bootcamps/${bootcampId}/register`);
      return res.data;
    },
    onSuccess: () => {
      toast.success("Enrolled in Bootcamp! Academy trainer notified.");
      queryClient.invalidateQueries({ queryKey: ["community-bootcamps"] });
    },
    onError: (err) => {
      toast.error(extractErrorMessage(err, "Enrollment failed."));
    },
  });

  // Send Chat Message Mutation
  const sendMessageMutation = useMutation({
    mutationFn: async () => {
      if (!activeRoomId || !chatInput.trim()) return;
      const res = await apiClient.post(`/community/chat/rooms/${activeRoomId}/messages`, {
        message_text: chatInput.trim(),
      });
      return res.data;
    },
    onSuccess: () => {
      setChatInput("");
      queryClient.invalidateQueries({ queryKey: ["community-chat-messages", activeRoomId] });
    },
  });

  // Create Feed Post Mutation
  const createPostMutation = useMutation({
    mutationFn: async () => {
      const res = await apiClient.post("/community/posts", {
        title: postTitle,
        content: postContent,
        sport_name: postSport,
        looking_for_players: true,
        players_needed: postPlayersNeeded,
        activity_time: postTime,
      });
      return res.data;
    },
    onSuccess: () => {
      toast.success("Game lobby post published to community wall!");
      setIsCreatePostOpen(false);
      queryClient.invalidateQueries({ queryKey: ["community-posts"] });
      setPostTitle("");
      setPostContent("");
    },
    onError: (err) => {
      toast.error(extractErrorMessage(err, "Failed to publish post."));
    },
  });

  if (status !== "authenticated" || !user) {
    return (
      <div className="flex min-h-screen w-full items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Top Marketplace Header */}
      <header className="sticky top-0 z-10 flex h-16 items-center justify-between border-b bg-background/95 px-4 backdrop-blur sm:px-8">
        <div className="flex items-center gap-3 font-bold">
          <div className="flex size-9 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow">
            <Trophy className="size-5" />
          </div>
          <div className="leading-tight">
            <div className="flex items-center gap-2">
              <span className="text-base font-bold tracking-tight">PlayCampus Community</span>
              <Badge className="bg-primary/15 text-primary border-primary/20 text-[10px]">
                Marketplace
              </Badge>
            </div>
            <p className="text-xs font-normal text-muted-foreground">
              Sports facilities, squads, live chats & academies
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
          >
            <Sun className="h-4 w-4 scale-100 dark:scale-0" />
            <Moon className="absolute h-4 w-4 scale-0 dark:scale-100" />
          </Button>

          <div className="flex items-center gap-2 border-l pl-3">
            <Avatar className="h-8 w-8">
              <AvatarFallback className="bg-primary text-primary-foreground text-xs font-bold">
                {user.name.slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="hidden sm:flex flex-col text-left text-xs">
              <span className="font-semibold text-foreground leading-tight">{user.name}</span>
              <span className="text-[10px] text-muted-foreground">{roleLabel(user.role)}</span>
            </div>
            <Button variant="ghost" size="icon" onClick={() => logout()}>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Navigation Subheader */}
      <div className="border-b bg-muted/30 px-4 sm:px-8">
        <div className="mx-auto max-w-7xl">
          <Tabs value={activeMainTab} onValueChange={setActiveMainTab} className="w-full">
            <TabsList className="h-12 bg-transparent gap-2 p-0">
              <TabsTrigger
                value="facilities"
                className="data-[state=active]:bg-background data-[state=active]:shadow-sm rounded-lg px-4 text-xs font-medium"
              >
                <Building2 className="mr-2 h-4 w-4 text-primary" /> Grounds & Facility Booking
              </TabsTrigger>
              <TabsTrigger
                value="squads"
                className="data-[state=active]:bg-background data-[state=active]:shadow-sm rounded-lg px-4 text-xs font-medium"
              >
                <Users className="mr-2 h-4 w-4 text-emerald-500" /> Community Squads & Teams
              </TabsTrigger>
              <TabsTrigger
                value="bootcamps"
                className="data-[state=active]:bg-background data-[state=active]:shadow-sm rounded-lg px-4 text-xs font-medium"
              >
                <Award className="mr-2 h-4 w-4 text-amber-500" /> Bootcamps & Academies
              </TabsTrigger>
              <TabsTrigger
                value="chat"
                className="data-[state=active]:bg-background data-[state=active]:shadow-sm rounded-lg px-4 text-xs font-medium"
              >
                <MessageSquare className="mr-2 h-4 w-4 text-blue-500" /> Live Match Chat
              </TabsTrigger>
              <TabsTrigger
                value="feed"
                className="data-[state=active]:bg-background data-[state=active]:shadow-sm rounded-lg px-4 text-xs font-medium"
              >
                <Sparkles className="mr-2 h-4 w-4 text-purple-500" /> Player Lobby & Feed
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* Main Content Area */}
      <main className="mx-auto max-w-7xl p-4 sm:p-8">
        {/* TAB 1: Facilities & Ground Booking */}
        {activeMainTab === "facilities" && (
          <FacilityBookingExplorer
            schools={schools || []}
            selectedSchool={selectedSchool}
            setSelectedSchool={setSelectedSchool}
            query={query}
            setQuery={setQuery}
          />
        )}

        {/* TAB 2: Community Squads & Teams */}
        {activeMainTab === "squads" && (
          <div className="flex flex-col gap-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold tracking-tight">Community Squads & Nearby Teams</h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Form squads with nearby players, schedule matches, and request to join friendly sports teams.
                </p>
              </div>

              <div className="flex items-center gap-3">
                <Select value={squadSportFilter} onValueChange={(val) => { if (val) setSquadSportFilter(val); }}>
                  <SelectTrigger className="h-9 w-36 text-xs bg-card">
                    <SelectValue placeholder="Filter Sport" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">All Sports</SelectItem>
                    <SelectItem value="Football">Football</SelectItem>
                    <SelectItem value="Badminton">Badminton</SelectItem>
                    <SelectItem value="Cricket">Cricket</SelectItem>
                    <SelectItem value="Basketball">Basketball</SelectItem>
                  </SelectContent>
                </Select>

                <Button onClick={() => setIsCreateSquadOpen(true)} className="gap-1.5 h-9 text-xs">
                  <Plus className="h-4 w-4" /> Create Squad
                </Button>
              </div>
            </div>

            {squadsLoading ? (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-48 w-full rounded-xl" />
                ))}
              </div>
            ) : squads?.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-xs text-muted-foreground">
                  No community squads found. Be the first to create one!
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {squads?.map((squad) => (
                  <Card key={squad.id} className="border-border/60 flex flex-col justify-between hover:shadow-md transition-all">
                    <CardHeader className="p-4 pb-2">
                      <div className="flex items-center justify-between">
                        <Badge variant="outline" className="text-[10px] font-semibold text-primary">
                          {squad.sport_name}
                        </Badge>
                        <Badge className="bg-muted text-muted-foreground text-[10px]">
                          {squad.skill_level}
                        </Badge>
                      </div>
                      <CardTitle className="text-base font-bold mt-2">{squad.name}</CardTitle>
                      <CardDescription className="text-xs flex items-center gap-1">
                        <MapPin className="h-3 w-3 text-muted-foreground" />
                        {squad.home_ground || "DPS Ground"} &bull; {squad.city || "New Delhi"}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="p-4 pt-1 pb-3">
                      <p className="text-xs text-muted-foreground line-clamp-2">
                        {squad.description || "Active weekend community squad organizing friendly matches."}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-3">
                        <Users className="h-3.5 w-3.5 text-primary" />
                        <span>
                          <strong className="text-foreground">{squad.member_count}</strong> / {squad.max_members} Members
                        </span>
                      </div>
                    </CardContent>
                    <CardFooter className="p-4 pt-0">
                      {squad.is_member ? (
                        <Button variant="secondary" size="sm" disabled className="w-full h-8 text-xs font-semibold">
                          <Check className="mr-1.5 h-3.5 w-3.5 text-emerald-500" />
                          {squad.membership_role === "CAPTAIN" ? "Squad Captain" : "Member"}
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => setJoiningSquad(squad)}
                          className="w-full h-8 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                          <UserPlus className="mr-1.5 h-3.5 w-3.5" /> Request to Join
                        </Button>
                      )}
                    </CardFooter>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* TAB 3: Bootcamps & Academies */}
        {activeMainTab === "bootcamps" && (
          <div className="flex flex-col gap-6">
            <div>
              <h2 className="text-xl font-bold tracking-tight">Sports Bootcamps & Training Academies</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Join structured monthly clinics, karate dojos, swimming camps, and football striker coaching.
              </p>
            </div>

            {bootcampsLoading ? (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {[1, 2].map((i) => (
                  <Skeleton key={i} className="h-60 w-full rounded-xl" />
                ))}
              </div>
            ) : (
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {bootcamps?.map((bc) => (
                  <Card key={bc.id} className="border-border/60 flex flex-col justify-between hover:shadow-lg transition-all bg-card/80">
                    <CardHeader className="p-5 pb-3">
                      <div className="flex items-center justify-between">
                        <Badge className="bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20 text-[10px]">
                          {bc.sport_name} Academy
                        </Badge>
                        <span className="text-base font-black text-primary">
                          ₹{bc.price_inr.toLocaleString()}
                          <span className="text-[10px] font-normal text-muted-foreground">/mo</span>
                        </span>
                      </div>
                      <CardTitle className="text-lg font-bold mt-2 leading-tight">
                        {bc.title}
                      </CardTitle>
                      <CardDescription className="text-xs font-medium text-foreground">
                        Coach / Sensei: <span className="text-primary">{bc.trainer_name}</span>
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="p-5 pt-0 pb-4 space-y-3">
                      <p className="text-xs text-muted-foreground">{bc.description}</p>
                      <div className="rounded-lg bg-muted/40 p-2.5 text-xs space-y-1">
                        <div className="flex items-center gap-1.5 text-muted-foreground font-mono text-[11px]">
                          <Clock className="h-3.5 w-3.5 text-primary" /> {bc.schedule_details}
                        </div>
                        <div className="flex items-center gap-1.5 text-muted-foreground text-[11px]">
                          <CalendarDays className="h-3.5 w-3.5 text-primary" /> {bc.start_date} to {bc.end_date}
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        <strong className="text-foreground">{bc.registered_count}</strong> of {bc.max_participants} seats enrolled
                      </div>
                    </CardContent>
                    <CardFooter className="p-5 pt-0">
                      {bc.is_registered ? (
                        <Button variant="secondary" size="sm" disabled className="w-full text-xs">
                          <Check className="mr-1.5 h-3.5 w-3.5 text-emerald-500" /> Enrolled
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => registerBootcampMutation.mutate(bc.id)}
                          disabled={registerBootcampMutation.isPending}
                          className="w-full text-xs font-semibold bg-amber-600 hover:bg-amber-700 text-white"
                        >
                          <Award className="mr-1.5 h-3.5 w-3.5" /> Register for Bootcamp (₹{bc.price_inr})
                        </Button>
                      )}
                    </CardFooter>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* TAB 4: Live Chat Engine */}
        {activeMainTab === "chat" && (
          <div className="grid gap-6 md:grid-cols-12 h-[600px] border rounded-xl overflow-hidden bg-card">
            {/* Rooms List */}
            <div className="md:col-span-4 border-r flex flex-col h-full bg-muted/20">
              <div className="p-3 border-b">
                <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Active Channels & Squad Chats
                </span>
              </div>
              <div className="flex-1 overflow-y-auto p-2 space-y-1">
                {chatRooms?.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => setActiveRoomId(r.id)}
                    className={`w-full text-left p-2.5 rounded-lg text-xs transition-all flex items-center justify-between ${
                      activeRoomId === r.id
                        ? "bg-primary text-primary-foreground font-semibold"
                        : "hover:bg-muted text-foreground"
                    }`}
                  >
                    <div className="flex items-center gap-2 truncate">
                      <MessageSquare className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">{r.name}</span>
                    </div>
                    <Badge
                      variant={activeRoomId === r.id ? "outline" : "secondary"}
                      className="text-[9px] px-1 py-0"
                    >
                      {r.room_type}
                    </Badge>
                  </button>
                ))}
              </div>
            </div>

            {/* Messages Area */}
            <div className="md:col-span-8 flex flex-col h-full">
              <div className="p-3 border-b flex items-center justify-between bg-card">
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4 text-primary" />
                  <span className="text-sm font-semibold">
                    {chatRooms?.find((r) => r.id === activeRoomId)?.name || "Live Chat"}
                  </span>
                </div>
                <Badge variant="outline" className="text-[10px] text-emerald-500 border-emerald-500/30">
                  Real-time Sync
                </Badge>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {activeMessages?.length === 0 ? (
                  <div className="text-center py-12 text-xs text-muted-foreground">
                    No messages yet. Send the first message!
                  </div>
                ) : (
                  activeMessages?.map((msg) => {
                    const isMe = msg.sender_id === user.id;
                    return (
                      <div
                        key={msg.id}
                        className={`flex flex-col ${isMe ? "items-end" : "items-start"}`}
                      >
                        <span className="text-[10px] text-muted-foreground px-1 mb-0.5">
                          {msg.sender_name}
                        </span>
                        <div
                          className={`max-w-md rounded-xl px-3 py-2 text-xs ${
                            isMe
                              ? "bg-primary text-primary-foreground rounded-br-none"
                              : "bg-muted text-foreground rounded-bl-none"
                          }`}
                        >
                          {msg.message_text}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              <div className="p-3 border-t bg-card flex items-center gap-2">
                <Input
                  placeholder="Type match update or message..."
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && sendMessageMutation.mutate()}
                  className="text-xs"
                />
                <Button
                  size="sm"
                  onClick={() => sendMessageMutation.mutate()}
                  disabled={sendMessageMutation.isPending || !chatInput.trim()}
                  className="text-xs"
                >
                  <Send className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* TAB 5: Player Lobby & Feed */}
        {activeMainTab === "feed" && (
          <div className="flex flex-col gap-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold tracking-tight">Looking for Players & Game Lobby</h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Need extra players for a football match or doubles badminton? Post on the community wall!
                </p>
              </div>
              <Button onClick={() => setIsCreatePostOpen(true)} className="gap-1.5 h-9 text-xs">
                <Plus className="h-4 w-4" /> Post Game Lobby
              </Button>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              {feedPosts?.map((post) => (
                <Card key={post.id} className="border-border/60">
                  <CardHeader className="p-4 pb-2">
                    <div className="flex items-center justify-between">
                      <Badge className="bg-primary/10 text-primary border-primary/20 text-[10px]">
                        {post.sport_name}
                      </Badge>
                      <Badge variant="outline" className="text-emerald-600 border-emerald-500/30 text-[10px]">
                        Need {post.players_needed} Players
                      </Badge>
                    </div>
                    <CardTitle className="text-base font-bold mt-2">{post.title}</CardTitle>
                    <CardDescription className="text-xs">
                      Posted by <strong className="text-foreground">{post.author_name}</strong> &bull; {post.activity_time}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="p-4 pt-1">
                    <p className="text-xs text-muted-foreground">{post.content}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Create Squad Modal */}
      <Dialog open={isCreateSquadOpen} onOpenChange={setIsCreateSquadOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-emerald-500" /> Create Community Squad
            </DialogTitle>
            <DialogDescription className="text-xs">
              Create an open or invite-only team to organize weekend games and book grounds together.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label className="text-xs">Squad Name *</Label>
              <Input
                placeholder="e.g. South Delhi Football Club"
                value={newSquadName}
                onChange={(e) => setNewSquadName(e.target.value)}
                className="text-xs"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Sport</Label>
                <Select value={newSquadSport} onValueChange={(val) => { if (val) setNewSquadSport(val); }}>
                  <SelectTrigger className="text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Football">Football</SelectItem>
                    <SelectItem value="Badminton">Badminton</SelectItem>
                    <SelectItem value="Cricket">Cricket</SelectItem>
                    <SelectItem value="Basketball">Basketball</SelectItem>
                    <SelectItem value="Tennis">Tennis</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Skill Level</Label>
                <Select value={newSquadSkill} onValueChange={(val) => { if (val) setNewSquadSkill(val); }}>
                  <SelectTrigger className="text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="All Levels">All Levels</SelectItem>
                    <SelectItem value="Beginner">Beginner</SelectItem>
                    <SelectItem value="Intermediate">Intermediate</SelectItem>
                    <SelectItem value="Advanced">Advanced</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Home Ground / Preferred School</Label>
              <Input
                placeholder="e.g. DPS Main Ground"
                value={newSquadGround}
                onChange={(e) => setNewSquadGround(e.target.value)}
                className="text-xs"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Description</Label>
              <Textarea
                placeholder="Tell players when you play, squad rules, and match frequency..."
                value={newSquadDesc}
                onChange={(e) => setNewSquadDesc(e.target.value)}
                rows={2}
                className="text-xs"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateSquadOpen(false)} className="text-xs">
              Cancel
            </Button>
            <Button
              onClick={() => createSquadMutation.mutate()}
              disabled={createSquadMutation.isPending || !newSquadName}
              className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {createSquadMutation.isPending ? "Creating..." : "Launch Squad"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Join Squad Modal */}
      <Dialog open={!!joiningSquad} onOpenChange={(open) => !open && setJoiningSquad(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-emerald-500" /> Join {joiningSquad?.name}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Send a note to the Squad Captain to join upcoming games.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label className="text-xs">Introductory Message</Label>
              <Textarea
                value={joinMessage}
                onChange={(e) => setJoinMessage(e.target.value)}
                rows={3}
                className="text-xs"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setJoiningSquad(null)} className="text-xs">
              Cancel
            </Button>
            <Button
              onClick={() => joinSquadMutation.mutate()}
              disabled={joinSquadMutation.isPending}
              className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {joinSquadMutation.isPending ? "Sending Request..." : "Join Squad"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Feed Post Modal */}
      <Dialog open={isCreatePostOpen} onOpenChange={setIsCreatePostOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-500" /> Post Looking-For-Players Lobby
            </DialogTitle>
            <DialogDescription className="text-xs">
              Invite nearby players to join your scheduled match or session.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label className="text-xs">Lobby Title *</Label>
              <Input
                placeholder="e.g. Need 2 midfielders for Sunday 6 PM football"
                value={postTitle}
                onChange={(e) => setPostTitle(e.target.value)}
                className="text-xs"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Sport</Label>
                <Select value={postSport} onValueChange={(val) => { if (val) setPostSport(val); }}>
                  <SelectTrigger className="text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Football">Football</SelectItem>
                    <SelectItem value="Badminton">Badminton</SelectItem>
                    <SelectItem value="Cricket">Cricket</SelectItem>
                    <SelectItem value="Basketball">Basketball</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Players Needed</Label>
                <Input
                  type="number"
                  min={1}
                  max={20}
                  value={postPlayersNeeded}
                  onChange={(e) => setPostPlayersNeeded(Number(e.target.value))}
                  className="text-xs"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Match Date & Time</Label>
              <Input
                placeholder="e.g. Sunday 6:00 PM - 8:00 PM"
                value={postTime}
                onChange={(e) => setPostTime(e.target.value)}
                className="text-xs"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Match Details & Pitch Info</Label>
              <Textarea
                placeholder="Details on ground location, format (7v7), and split costs..."
                value={postContent}
                onChange={(e) => setPostContent(e.target.value)}
                rows={2}
                className="text-xs"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreatePostOpen(false)} className="text-xs">
              Cancel
            </Button>
            <Button
              onClick={() => createPostMutation.mutate()}
              disabled={createPostMutation.isPending || !postTitle}
              className="text-xs bg-purple-600 hover:bg-purple-700 text-white"
            >
              {createPostMutation.isPending ? "Posting..." : "Publish to Wall"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FacilityBookingExplorer({
  schools,
  selectedSchool,
  setSelectedSchool,
  query,
  setQuery,
}: {
  schools: PublicSchool[];
  selectedSchool: PublicSchool | null;
  setSelectedSchool: (s: PublicSchool | null) => void;
  query: string;
  setQuery: (q: string) => void;
}) {
  const { data: units, isLoading: unitsLoading } = useMarketplaceUnits(selectedSchool?.id ?? null);
  const [selectedUnit, setSelectedUnit] = useState<MarketplaceUnit | null>(null);
  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().slice(0, 10));
  const { data: availability, isLoading: availLoading } = useAvailability(
    selectedSchool?.id ?? null,
    selectedUnit?.unit_id ?? null,
    selectedDate
  );
  const createBooking = useCreateBooking();
  const payBooking = usePayBooking();
  const { data: myBookings } = useMyBookings();

  const [bookingSlot, setBookingSlot] = useState<{ start: string; end: string } | null>(null);

  const bookAndPay = async () => {
    if (!selectedSchool || !selectedUnit || !bookingSlot) return;
    try {
      const booking = await createBooking.mutateAsync({
        school_id: selectedSchool.id,
        unit_id: selectedUnit.unit_id,
        start_at: bookingSlot.start,
        end_at: bookingSlot.end,
      });
      await payBooking.mutateAsync(booking.id);
      toast.success("Facility slot booked & paid! Entry QR code generated.");
      setBookingSlot(null);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Booking failed"));
    }
  };

  return (
    <div className="flex flex-col gap-6">
      {/* School Selector */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold tracking-tight">Sports Grounds & Facility Schedules</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Book certified floodlit turfs, synthetic courts, and pools with verified guard check-in.
          </p>
        </div>

        <div className="flex items-center gap-3 w-full md:w-auto">
          <Select
            value={selectedSchool?.id || ""}
            onValueChange={(id) => {
              const s = schools.find((x) => x.id === id);
              if (s) setSelectedSchool(s);
            }}
          >
            <SelectTrigger className="h-9 w-64 text-xs bg-card">
              <SelectValue placeholder="Select School" />
            </SelectTrigger>
            <SelectContent>
              {schools.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name} ({s.code})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Available Playing Units */}
      {unitsLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-40 w-full rounded-xl" />
          ))}
        </div>
      ) : units?.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-xs text-muted-foreground">
            No public monetized booking slots currently available for this campus.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {units?.map((u) => {
            const isSelected = selectedUnit?.unit_id === u.unit_id;
            return (
              <Card
                key={u.unit_id}
                className={`border cursor-pointer transition-all ${
                  isSelected ? "border-primary ring-2 ring-primary/20 shadow-md" : "border-border/60 hover:border-primary/40"
                }`}
                onClick={() => setSelectedUnit(u)}
              >
                <CardHeader className="p-4 pb-2">
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className="text-[10px]">
                      {u.facility_name}
                    </Badge>
                    <span className="text-xs font-bold text-primary">
                      ₹{u.windows?.[0]?.price_per_hour ?? 500}/hr
                    </span>
                  </div>
                  <CardTitle className="text-base font-bold mt-2">{u.unit_name}</CardTitle>
                  <CardDescription className="text-xs">Type: {u.unit_type}</CardDescription>
                </CardHeader>
                <CardContent className="p-4 pt-1 pb-3">
                  <span className="text-xs text-muted-foreground">
                    Available {u.windows?.length ?? 0} days/week &bull; Evening & Weekend slots
                  </span>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Slot Availability & Booking Area */}
      {selectedUnit && (
        <Card className="border-primary/30 bg-card/80">
          <CardHeader className="p-4 pb-2">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <CardTitle className="text-base font-bold">
                  Select Slot for {selectedUnit.unit_name}
                </CardTitle>
                <CardDescription className="text-xs">
                  Choose a date to view live availability and book slot.
                </CardDescription>
              </div>
              <Input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-40 h-8 text-xs bg-background"
              />
            </div>
          </CardHeader>
          <CardContent className="p-4 pt-2">
            {availLoading ? (
              <Skeleton className="h-16 w-full" />
            ) : !availability?.slots || availability.slots.length === 0 ? (
              <div className="text-xs text-muted-foreground py-4 text-center">
                No slots open for online booking on this date.
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {availability.slots.map((slot, idx) => (
                  <Button
                    key={idx}
                    variant="outline"
                    onClick={() =>
                      setBookingSlot({
                        start: slot.start_at,
                        end: slot.end_at,
                      })
                    }
                    className="h-12 text-xs flex flex-col items-center justify-center p-1"
                  >
                    <span className="font-semibold">{slot.start_at.slice(11, 16)} - {slot.end_at.slice(11, 16)}</span>
                    <span className="text-[10px] text-muted-foreground">₹{slot.price_per_hour} INR</span>
                  </Button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Confirm Booking Dialog */}
      <Dialog open={!!bookingSlot} onOpenChange={(open) => !open && setBookingSlot(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Wallet className="h-4 w-4 text-primary" /> Confirm Ground Booking
            </DialogTitle>
            <DialogDescription className="text-xs">
              Instant reservation and gate check-in pass.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2 text-xs">
            <div className="rounded-lg bg-muted/40 p-3 space-y-1">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Facility:</span>
                <span className="font-semibold">{selectedUnit?.unit_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Date:</span>
                <span className="font-semibold">{selectedDate}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Time:</span>
                <span className="font-semibold">
                  {bookingSlot?.start.slice(11, 16)} - {bookingSlot?.end.slice(11, 16)}
                </span>
              </div>
              <div className="flex justify-between border-t pt-1 font-bold text-sm text-primary">
                <span>Total Amount:</span>
                <span>₹500 INR</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBookingSlot(null)} className="text-xs">
              Cancel
            </Button>
            <Button
              onClick={bookAndPay}
              disabled={createBooking.isPending || payBooking.isPending}
              className="text-xs bg-primary"
            >
              {createBooking.isPending || payBooking.isPending ? "Processing..." : "Pay & Generate QR"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
